import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { ndaTypeService, buildFileUrl } from "../../services/api";

const NDATypeManagement = () => {
  const [ndaTypes, setNdaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [editingNdaType, setEditingNdaType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [removeDocument, setRemoveDocument] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isActive: true,
  });

  useEffect(() => {
    loadNdaTypes();
  }, []);

  const loadNdaTypes = async () => {
    try {
      setLoading(true);
      const response = await ndaTypeService.getAll();
      setNdaTypes(response.data.data);
      setError("");
    } catch (err) {
      console.error("Error loading NDA Types:", err);
      setError("Error al cargar tipos de NDA");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (ndaType = null) => {
    if (ndaType) {
      setEditingNdaType(ndaType);
      setFormData({
        title: ndaType.title,
        description: ndaType.description || "",
        isActive: ndaType.isActive,
      });
      setSelectedFile(null);
      setRemoveDocument(false);
    } else {
      setEditingNdaType(null);
      setFormData({
        title: "",
        description: "",
        isActive: true,
      });
      setSelectedFile(null);
      setRemoveDocument(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNdaType(null);
    setFormData({
      title: "",
      description: "",
      isActive: true,
    });
    setSelectedFile(null);
    setRemoveDocument(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setRemoveDocument(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError("El título es requerido");
      return;
    }

    try {
      setSubmitting(true);
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("isActive", formData.isActive ? "true" : "false");

      if (selectedFile) {
        formDataToSend.append("document", selectedFile);
      }

      if (editingNdaType) {
        if (removeDocument) {
          formDataToSend.append("removeDocument", "true");
        }
        await ndaTypeService.update(editingNdaType.id, formDataToSend);
        setSuccess("Tipo de NDA actualizado exitosamente");
      } else {
        await ndaTypeService.create(formDataToSend);
        setSuccess("Tipo de NDA creado exitosamente");
      }

      handleCloseDialog();
      loadNdaTypes();
    } catch (err) {
      console.error("Error saving NDA Type:", err);
      setError(err.response?.data?.error || "Error al guardar tipo de NDA");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este tipo de NDA?")) return;

    try {
      await ndaTypeService.delete(id);
      setSuccess("Tipo de NDA eliminado exitosamente");
      loadNdaTypes();
    } catch (err) {
      console.error("Error deleting NDA Type:", err);
      setError("Error al eliminar tipo de NDA");
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            Gestión de Tipos de NDA
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadNdaTypes}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nuevo Tipo de NDA
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Paper>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell>
                      <strong>Título</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Descripción</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Estado</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Acciones</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ndaTypes.map((ndaType) => (
                    <TableRow key={ndaType.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ndaType.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {ndaType.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ndaType.isActive ? "Activo" : "Inactivo"}
                          color={ndaType.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(ndaType)}
                          title="Editar"
                          sx={{ color: "#2e7d32" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(ndaType.id)}
                          title="Eliminar"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingNdaType ? "Editar Tipo de NDA" : "Nuevo Tipo de NDA"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Título *"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
          />
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Documento Asociado (Opcional)
            </Typography>
            <input
              accept=".pdf,.doc,.docx,.txt"
              style={{ display: "none" }}
              id="nda-type-document-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="nda-type-document-upload">
              <Button
                variant="outlined"
                component="span"
                size="small"
                sx={{ mr: 2 }}
              >
                {selectedFile
                  ? selectedFile.name
                  : editingNdaType?.documentUrl
                  ? "Cambiar archivo"
                  : "Seleccionar archivo"}
              </Button>
            </label>
            {editingNdaType?.documentUrl && !selectedFile && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  href={buildFileUrl(editingNdaType.documentUrl, "nda-types")}
                  target="_blank"
                  sx={{ mr: 1 }}
                >
                  Ver documento actual
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={removeDocument}
                      onChange={(e) => setRemoveDocument(e.target.checked)}
                    />
                  }
                  label="Eliminar documento"
                />
              </>
            )}
            {selectedFile && (
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Archivo seleccionado: {selectedFile.name}
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={
              <Switch
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
              />
            }
            label="Activo"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NDATypeManagement;
